/** Component dùng chung của các khối: nút nghe, dòng từ, biểu đồ cao độ, khung bắt lỗi khối. */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Alert, Button, Tooltip } from "antd";
import { SoundOutlined } from "@ant-design/icons";

import { HAN_FONT, chuOf, playAudio, toneColor, useView } from "./shared";
import type { WordObj } from "./types";

export function AudioButton({ src, label = "Nghe" }: { src?: string; label?: string }) {
  return (
    <Tooltip title={label}>
      <Button
        size="small"
        shape="circle"
        icon={<SoundOutlined />}
        disabled={!src}
        aria-label={label}
        onClick={() => void playAudio(src)}
      />
    </Tooltip>
  );
}

export function Pinyin({ py, td }: { py: string[]; td?: number[] }) {
  return (
    <span>
      {py.map((p, i) => (
        <span key={i} style={{ color: toneColor(td?.[i]) }}>
          {p}
          {i < py.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
}

export function WordLine({ w, size = 18 }: { w: WordObj; size?: number }) {
  const v = useView();
  return (
    <div>
      <div style={{ fontFamily: HAN_FONT, fontSize: size, lineHeight: 1.3 }}>
        {chuOf(w, v.phon)}
      </div>
      {v.py && w.py?.length ? (
        <div style={{ fontSize: 14 }}>
          <Pinyin py={w.py} td={w.td} />
        </div>
      ) : null}
      {v.hv && w.hv ? (
        <div style={{ fontSize: 13, color: "#8c8c8c", fontStyle: "italic" }}>{w.hv}</div>
      ) : null}
    </div>
  );
}

function pathOf(pts: [number, number][], W: number, H: number, pad: number): string {
  return pts
    .map(
      ([x, y], i) =>
        (i ? "L" : "M") +
        (pad + x * (W - 2 * pad)).toFixed(1) +
        " " +
        (H / 2 - (Math.max(-10, Math.min(10, y)) * (H / 2 - pad)) / 10).toFixed(1),
    )
    .join(" ");
}

export function PitchChart({
  model,
  user,
  color,
  height = 170,
}: {
  model: [number, number][];
  user?: [number, number][] | null;
  color: string;
  height?: number;
}) {
  const W = 420;
  const H = height;
  const pad = 14;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Đường cao độ"
      style={{ width: "100%", maxWidth: 420, height: "auto", display: "block" }}
    >
      <rect x={0} y={0} width={W} height={H} fill="#fafafa" rx={8} />
      {[0, 1, 2, 3, 4].map((k) => {
        const y = pad + (k * (H - 2 * pad)) / 4;
        return (
          <line
            key={k}
            x1={pad}
            x2={W - pad}
            y1={y}
            y2={y}
            stroke="#e8e8e8"
            strokeDasharray={k === 2 ? "0" : "3 4"}
          />
        );
      })}
      {model.length > 0 && (
        <path
          d={pathOf(model, W, H, pad)}
          fill="none"
          stroke={color}
          strokeWidth={5}
          strokeOpacity={0.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {user && user.length > 0 && (
        <path
          d={pathOf(user, W, H, pad)}
          fill="none"
          stroke="#1f2329"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

// ─── Lỗi 1 khối không làm vỡ cả bài ──────────────────────────────────────

export class BlockErrorBoundary extends Component<
  { kieu: string; children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[content-blocks] khối ${this.props.kieu} lỗi`, error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <Alert
          type="warning"
          showIcon
          message={`Không hiển thị được hoạt động này (${this.props.kieu}).`}
          description="Các phần khác của bài vẫn học được bình thường."
        />
      );
    }
    return this.props.children;
  }
}
